import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import BricksSidebar from './BricksSidebar';
// import AIActionTray from './AIActionTray'; // DISABLED: Focus on tools first
import {
  IconClipboard,
  IconUsers,
  IconCalendar,
  IconCalendarStats,
  IconPackage,
  IconTool,
  IconBus,
  IconAlertCircle,
  IconTree,
  IconCurrencyDollar,
  IconReceipt,
  IconCash,
  IconTrendingUp,
  IconUserPlus,
  IconAward,
  IconMessage,
  IconShirt,
  IconChevronDown,
  IconChevronRight,
  IconSettings,
  IconMapPin,
  IconShield,
  IconChartLine,
  IconPalette,
  IconStar,
  IconSearch,
  IconRun,
  IconEye,
  IconCoin,
  IconReportMoney,
  IconMail,
  IconCrown,
  IconTrophy,
  IconBook,
  IconBulb,
  IconWorld,
  IconHome,
  IconSchool,
  IconBriefcase,
  IconAlertTriangle,
  IconCar,
  IconRobot,
  IconSpeakerphone,
  IconList,
  IconRocket,
  IconTerminal2
} from '@tabler/icons-react';
import { GARAGES } from '../constants/garages';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const ICON_MAP = {
    IconClipboard,
    IconUsers,
    IconCalendar,
    IconCalendarStats,
    IconPackage,
    IconTool,
    IconBus,
    IconAlertCircle,
    IconTree,
    IconCurrencyDollar,
    IconReceipt,
    IconCash,
    IconTrendingUp,
    IconUserPlus,
    IconAward,
    IconMessage,
    IconShirt,
    IconChevronDown,
    IconChevronRight,
    IconSettings,
    IconMapPin,
    IconShield,
    IconChartLine,
    IconPalette,
    IconStar,
    IconUserSearch: IconSearch,
    IconRun,
    IconEye,
    IconCoin,
    IconReportMoney,
    IconMail,
    IconCrown,
    IconTrophy,
    IconBook,
    IconBulb,
    IconWorld,
    IconHome,
    IconSchool,
    IconBriefcase,
    IconAlertTriangle,
    IconCar,
    IconRobot,
    IconSpeakerphone,
    IconList,
    IconRocket,
    IconTerminal2,
    IconClipboardCheck: IconClipboard,
  };

// Metallic gold animations for garage headers
const sidebarAnimations = `
  @keyframes shimmer-flow {
    0% { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  @keyframes metallic-shine {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
`;

// Inject animations into document (only once)
if (typeof document !== 'undefined' && !document.getElementById('sidebar-animations')) {
  const style = document.createElement('style');
  style.id = 'sidebar-animations';
  style.innerHTML = sidebarAnimations;
  document.head.appendChild(style);
}

const normalizePath = (path) => {
  if (!path) return '/';
  return path.replace(/\/+$/, '') || '/';
};

const compareLotMatch = (currentPath, lotPath) => {
  if (!lotPath) return null;
  const normalizedLot = normalizePath(lotPath);
  const normalizedCurrent = normalizePath(currentPath);

  if (normalizedCurrent === normalizedLot) {
    return { score: 2, length: normalizedLot.length };
  }

  const lotWithSlash = normalizedLot.endsWith('/') ? normalizedLot : `${normalizedLot}/`;
  if (normalizedCurrent.startsWith(lotWithSlash)) {
    return { score: 1, length: normalizedLot.length };
  }

  return null;
};

export default function LotsSidebar({ collapsed, onMobileClose, currentView: _currentView }) {
  // Silence unused prop warning - kept for API compatibility
  void _currentView;

  // ALL HOOKS MUST BE CALLED AT THE TOP - before any conditional returns
  // Feature flag: Use new brick-based navigation (with search)
  const [useBrickNav, setUseBrickNav] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem('odv-brick-nav-enabled') === 'true';
  });

  // These hooks must be called unconditionally (React rules of hooks)
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userBadges, rolesReady } = useAuth();

  // Initialize from localStorage or default to 'operations' for admin, 'valet' for valets
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem('odv-expanded-garage');
    return saved ? [saved] : ['operations'];
  });

  // Track recently visited lots
  const [_recentLots, setRecentLots] = useState(() => {
    const saved = localStorage.getItem('odv-recent-lots');
    return saved ? JSON.parse(saved) : [];
  });

  // Accordion behavior: Close other garages when opening one
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const isCurrentlyExpanded = prev.includes(groupId);
      if (isCurrentlyExpanded) {
        // Close the garage
        return [];
      } else {
        // Open this garage, close all others (accordion)
        return [groupId];
      }
    });
  };

  // Save to localStorage whenever expandedGroups changes
  useEffect(() => {
    if (expandedGroups.length > 0) {
      localStorage.setItem('odv-expanded-garage', expandedGroups[0]);
    } else {
      localStorage.removeItem('odv-expanded-garage');
    }
  }, [expandedGroups]);

  const activeGarage = useMemo(() => {
    const currentPath = location.pathname;
    let bestMatch = null;

    GARAGES.forEach((garage) => {
      garage.lots.forEach((lot) => {
        const lotPath = lot.path || `/${garage.id}/${lot.id}`;
        const match = compareLotMatch(currentPath, lotPath);
        if (!match) return;

        const candidate = {
          garageId: garage.id,
          ...match
        };

        if (
          !bestMatch ||
          candidate.score > bestMatch.score ||
          (candidate.score === bestMatch.score && candidate.length > bestMatch.length)
        ) {
          bestMatch = candidate;
        }
      });
    });

    return bestMatch?.garageId || null;
  }, [location.pathname]);

  // Auto-expand disabled: lots collapse after selection, user re-clicks garage to see lots

  // Publish current lot/garage to global screen context for AI awareness
  // AND track recent lots for quick access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Find the active lot from current path
      const currentPath = location.pathname;
      let activeLot = null;
      let garageInfo = null;

      GARAGES.forEach((garage) => {
        garage.lots.forEach((lot) => {
          const lotPath = lot.path || `/${garage.id}/${lot.id}`;
          if (currentPath.startsWith(lotPath)) {
            activeLot = {
              id: lot.id,
              name: lot.name,
              path: lotPath,
              icon: lot.icon,
              garageId: garage.id,
              garageName: garage.name
            };
            garageInfo = {
              id: garage.id,
              name: garage.name
            };
          }
        });
      });

      window.__ODV_SCREEN_CONTEXT__ = {
        ...window.__ODV_SCREEN_CONTEXT__,
        currentGarage: garageInfo,
        currentLot: activeLot,
        activeGarageId: activeGarage
      };

      // Update recent lots (keep last 6, no duplicates)
      if (activeLot) {
        setRecentLots(prev => {
          const filtered = prev.filter(l => l.path !== activeLot.path);
          const updated = [activeLot, ...filtered].slice(0, 6);
          localStorage.setItem('odv-recent-lots', JSON.stringify(updated));
          return updated;
        });
      }
    }
  }, [location.pathname, activeGarage]);

  // Filter and sort garages - expanded garage goes to top
  const sortedGarages = useMemo(() => {
    const expandedId = expandedGroups[0];

    // Helper functions for badge checking
    const badges = userBadges || [];
    const hasBadge = (type) => badges.includes(type);
    const hasBadgePrefix = (prefix) => badges.some(
      (type) => type && (type === prefix || type.startsWith(`${prefix}-`))
    );
    const hasKOTBadge = hasBadge('kot') || hasBadgePrefix('kot');

    // Filter garages and their lots based on badges
    const filteredGarages = GARAGES.map(garage => {
      const hasFullGarageAccess = hasKOTBadge && (garage.id === 'operations' || garage.id === 'hr');

      const accessibleLots = garage.lots.filter(lot => {
        if (hasFullGarageAccess && !lot.strictBadges) return true;
        if (!lot.requiredBadges || lot.requiredBadges.length === 0) return true;
        return lot.requiredBadges.some(badge => badges.includes(badge));
      });

      return { ...garage, accessibleLots };
    }).filter(garage => garage.accessibleLots.length > 0);

    // Sort: expanded garage first, then maintain original order
    if (!expandedId) return filteredGarages;

    return [...filteredGarages].sort((a, b) => {
      if (a.id === expandedId) return -1;
      if (b.id === expandedId) return 1;
      return 0;
    });
  }, [expandedGroups, userBadges]);

  const toggleNavStyle = () => {
    const newValue = !useBrickNav;
    setUseBrickNav(newValue);
    localStorage.setItem('odv-brick-nav-enabled', String(newValue));
  };

  // EARLY RETURN: Now safe after all hooks have been called
  if (useBrickNav) {
    return (
      <div className="h-full flex flex-col">
        <BricksSidebar collapsed={collapsed} onMobileClose={onMobileClose} />
        {/* Nav Style Toggle */}
        {!collapsed && (
          <div className="absolute bottom-2 left-2 right-2 z-20">
            <button
              onClick={toggleNavStyle}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-900/90 hover:bg-gray-800 border border-gray-700 hover:border-vegas-gold/40 rounded-lg text-xs text-gray-400 hover:text-vegas-gold transition-all duration-200"
              title="Switch to Garage view"
            >
              <IconCar className="w-3.5 h-3.5" />
              <span>Garage View</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col relative overflow-hidden">
      {/* Fixed gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-600 via-gray-700 via-gray-800 to-gray-900" />
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        <nav className="flex-1 overflow-y-auto py-2 px-1.5 custom-scrollbar pb-16">
          <motion.div layout className="space-y-2">
            <AnimatePresence mode="popLayout">
              {sortedGarages.map((group) => {
                const GroupIcon = ICON_MAP[group.icon];
                const isExpanded = expandedGroups.includes(group.id);
                const accessibleLots = group.accessibleLots;

                const hasActiveLot = accessibleLots.some((item) => {
                  const lotPath = item.path || `/${group.id}/${item.id}`;
                  return !!compareLotMatch(location.pathname, lotPath);
                });

                return (
                  <motion.div
                    key={group.id}
                    layout
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{
                      layout: { type: "spring", stiffness: 350, damping: 30 },
                      opacity: { duration: 0.2 }
                    }}
                    className="mb-2"
                  >
                    {/* Garage/Department Header - Dropdown Bucket Button */}
                    {!collapsed && (
                      <motion.button
                        layout="position"
                        onClick={() => toggleGroup(group.id)}
                        className={`
                          w-full flex items-center justify-between px-2 py-1.5 rounded-lg
                          transition-all duration-300 relative overflow-hidden
                          ${hasActiveLot || isExpanded
                            ? 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 shadow-[0_0_15px_rgba(174,163,109,0.3)] border border-vegas-gold/40'
                            : 'bg-gradient-to-r from-gray-800/80 to-gray-700/60 hover:from-gray-800 hover:to-gray-700 border border-gray-700/50 hover:border-vegas-gold/30 hover:shadow-[0_0_10px_rgba(174,163,109,0.2)]'
                          }
                        `}
                      >
                        {/* Inner black border with padding */}
                        <div className="absolute inset-[1px] rounded-md border border-black/60 pointer-events-none z-10" />

                        {/* Metallic gold shimmer overlay */}
                        <div
                          className={`absolute inset-0 pointer-events-none ${hasActiveLot || isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}
                          style={{
                            background: 'linear-gradient(90deg, transparent 0%, rgba(174,163,109,0.1) 25%, rgba(212,175,55,0.2) 50%, rgba(174,163,109,0.1) 75%, transparent 100%)',
                            backgroundSize: '200% 100%',
                            animation: hasActiveLot || isExpanded ? 'shimmer-flow 3s linear infinite' : 'none'
                          }}
                        />
                        <div className="flex items-center space-x-2 relative z-10">
                          <GroupIcon
                            className="w-5 h-5"
                            style={{
                              color: '#D4AF37',
                              filter: 'drop-shadow(0 0 4px rgba(212,175,55,0.5))'
                            }}
                          />
                          <span
                            className="text-[13px] font-bold uppercase tracking-wide truncate"
                            style={{
                              background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 25%, #D4AF37 50%, #B8860B 75%, #D4AF37 100%)',
                              backgroundSize: '200% auto',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                              animation: hasActiveLot || isExpanded ? 'metallic-shine 3s linear infinite' : 'none',
                              textShadow: hasActiveLot || isExpanded ? '0 0 20px rgba(212,175,55,0.3)' : 'none'
                            }}
                          >
                            {group.name}
                          </span>
                        </div>
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          className="relative z-10"
                        >
                          <IconChevronDown
                            className="w-4 h-4"
                            style={{
                              color: '#D4AF37',
                              filter: 'drop-shadow(0 0 3px rgba(212,175,55,0.4))'
                            }}
                          />
                        </motion.div>
                      </motion.button>
                    )}

                    {/* Collapsed State - Just Icon */}
                    {collapsed && (
                      <button
                        onClick={() => toggleGroup(group.id)}
                        className={`
                          w-full flex items-center justify-center p-2 rounded-lg
                          transition-all duration-300 relative overflow-hidden
                          ${hasActiveLot
                            ? 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 shadow-[0_0_15px_rgba(174,163,109,0.3)] border border-vegas-gold/40'
                            : 'bg-gradient-to-r from-gray-800/80 to-gray-700/60 hover:from-gray-800 hover:to-gray-700 border border-gray-700/50 hover:border-vegas-gold/30 hover:shadow-[0_0_10px_rgba(174,163,109,0.2)]'
                          }
                        `}
                        title={group.name}
                      >
                        {/* Inner black border with padding */}
                        <div className="absolute inset-[1px] rounded-md border border-black/60 pointer-events-none z-10" />

                        {/* Metallic shimmer overlay for collapsed */}
                        {hasActiveLot && (
                          <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background: 'linear-gradient(90deg, transparent 0%, rgba(174,163,109,0.1) 25%, rgba(212,175,55,0.2) 50%, rgba(174,163,109,0.1) 75%, transparent 100%)',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer-flow 3s linear infinite'
                            }}
                          />
                        )}
                        <GroupIcon
                          className="w-5 h-5 relative z-10"
                          style={{
                            color: '#D4AF37',
                            filter: hasActiveLot ? 'drop-shadow(0 0 6px rgba(212,175,55,0.6))' : 'drop-shadow(0 0 3px rgba(212,175,55,0.3))'
                          }}
                        />
                      </button>
                    )}

                    {/* Lots Tree (Indented) - Animated */}
                    <AnimatePresence>
                      {(collapsed || isExpanded) && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className={`overflow-hidden mt-1 ${!collapsed ? 'pl-1.5 border-l border-gray-600/40 ml-3' : ''}`}
                        >
                          <div className="space-y-0.5">
                            {accessibleLots.map((item, index) => {
                              const Icon = ICON_MAP[item.icon];
                              const path = item.path || `/${group.id}/${item.id}`;
                              const match = compareLotMatch(location.pathname, path);
                              const isActive = !!match;

                              return (
                                <motion.button
                                  type="button"
                                  key={path}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  whileHover={{
                                    y: -2,
                                    scale: 1.02,
                                    transition: { duration: 0.15 }
                                  }}
                                  whileTap={{ scale: 0.98 }}
                                  transition={{ delay: index * 0.05, duration: 0.2 }}
                                  onClick={() => {
                                    navigate(path);
                                    setExpandedGroups([]); // Collapse after lot selection
                                    onMobileClose?.();
                                  }}
                                  className={`
                                    w-full group relative flex items-center px-2 py-1.5 rounded-md
                                    transition-all duration-200
                                    ${collapsed ? 'justify-center' : 'gap-2'}
                                    ${isActive
                                      ? 'bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 shadow-[0_4px_12px_rgba(0,0,0,0.4),0_0_12px_rgba(174,163,109,0.3)] border border-vegas-gold'
                                      : 'bg-gray-800/60 hover:bg-gray-700/80 border border-black hover:border-gray-800 shadow-[0_2px_4px_rgba(0,0,0,0.2)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.4),0_0_8px_rgba(174,163,109,0.15)]'
                                    }
                                  `}
                                  title={item.name}
                                >
                                  {/* Icon */}
                                  {Icon && (
                                    <Icon
                                      className={`h-5 w-5 flex-shrink-0 transition-all duration-200 ${
                                        isActive
                                          ? 'drop-shadow-[0_0_8px_rgba(174,163,109,0.8)]'
                                          : 'group-hover:drop-shadow-[0_0_6px_rgba(174,163,109,0.6)]'
                                      }`}
                                      style={{
                                        color: isActive ? '#B8935F' : '#6B4E35',
                                        filter: isActive
                                          ? 'drop-shadow(0 3px 4px rgba(0,0,0,0.8)) drop-shadow(0 1px 2px rgba(139,99,64,0.7)) drop-shadow(0 0 10px rgba(174,163,109,0.6))'
                                          : 'drop-shadow(0 2px 3px rgba(0,0,0,0.7)) drop-shadow(0 1px 2px rgba(107,68,35,0.6)) drop-shadow(0 0 5px rgba(139,99,64,0.3))'
                                      }}
                                    />
                                  )}

                                  {/* Text */}
                                  {!collapsed && (
                                    <span
                                      className={`text-[13px] font-medium tracking-normal truncate ${
                                        item.textColor === 'teal'
                                          ? 'text-teal-400 group-hover:text-teal-300'
                                          : `bg-clip-text text-transparent ${
                                              isActive
                                                ? 'bg-gradient-to-br from-[#E5C88A] via-[#C4A969] to-[#9B8357]'
                                                : 'bg-gradient-to-br from-[#B89D5F] via-[#9B8357] to-[#7A6344] group-hover:from-[#C4A969] group-hover:via-[#AEA36D] group-hover:to-[#8B7355] group-hover:drop-shadow-[0_0_3px_rgba(174,163,109,0.4)]'
                                            }`
                                      }`}
                                      style={item.textColor !== 'teal' ? {
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        ...(isActive && {
                                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6)) drop-shadow(0 0 8px rgba(229,200,138,0.4))',
                                        })
                                      } : {}}
                                    >
                                      {item.name}
                                    </span>
                                  )}
                                </motion.button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </nav>

        {/* Nav Style Toggle - Switch to Search view */}
        {!collapsed && (
          <div className="p-1.5 border-t border-gray-700/40">
            <button
              onClick={toggleNavStyle}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-1 bg-gray-800/80 hover:bg-gray-700 border border-gray-600 hover:border-vegas-gold/40 rounded-lg text-[12px] text-gray-400 hover:text-vegas-gold transition-all duration-200"
              title="Switch to Search view"
            >
              <IconSearch className="w-4 h-4" />
              <span>Search</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
